import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Row, Col, Dropdown } from 'react-bootstrap';
import { FaLock, FaLockOpen, FaChartBar } from 'react-icons/fa';
import TimeSeriesChart from './TimeSeriesChart';
import { getSearchHistogram } from 'SensitiveDataLandscape/LandscapeUtils';
import RiskCategories from './RiskCategories';
import FlaggedFields from './FlaggedFields';
import HubKpiStrip from './HubKpiStrip';
import ActivityHistory from './ActivityHistory';
import IdentityClusterPanel from './IdentityClusterPanel';
import MaskingPanel from './MaskingPanel';
import SubSourcesPanel from 'DataSources/SubSourcesPanel';
import { transformCategoryData } from './RiskCategories/util';
import {
  getProfileSource,
  getLatestDiscoverResults,
  findingsSummaryFromRunSummary,
  getLatestSuccessfulDiscoveryId,
  getFindingsSummary,
  normalizeRiskLevel,
} from './api';
import { getIdentityCluster } from './hubApi';
import { getDataSourceSummary } from 'Metadata/metaAnalysisApi';
import { startDiscoveryWithRule } from 'DataDiscovery/discoverApi';
import { startProfileJob } from 'Metadata/profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { engineLabels, EngineIcon, getSourceLabel, RISK_LEVELS } from 'Utils';
import { isFileBasedDatasourceType } from 'Utils/fileTypeUtils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import AppContext from 'Utils/AppContext';
import { STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import FetchContainer from 'Utils/FetchContainer';
import PageHeader from 'Utils/PageHeader';

// Top-level data-source category shown in the page subtitle. Mirrors the three platform
// categories (RDBMS / NoSQL / Files) rather than the old "<engine> database running on <cloud>".
const sourceCategoryLabel = (datasourceType) => {
  const type = datasourceType || '';
  if (type === 'NOSQL') return 'NoSQL';
  if (isFileBasedDatasourceType(type) || type.startsWith('FILE_')) return 'Files';
  return 'RDBMS';
};

// The Data Source Hub — the risk profile expanded with the meta-profile KPI strip,
// unified activity history, identity cluster (schema-copy locations), and masking
// role panel (mockup data-source-hub). The discovery-driven risk summary + time
// series render only when the source has been discovered; the hub panels render
// from their own endpoints regardless.
const DataSourceRiskProfile = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const { connections, setNotification } = useContext(AppContext) || {};
  // Guards double-clicks on the Actions ▾ start items (discover/profile fire API calls).
  const [actionBusy, setActionBusy] = useState(false);
  const [findings, setFindings] = useState([]);
  const [totalTables, setTotalTables] = useState(0);
  // Tables the run skipped pre-flight for holding zero rows — reported beside the processed
  // count so an empty schema doesn't read as "0 of 0 tables".
  const [emptyTables, setEmptyTables] = useState(0);
  const [totalColumns, setTotalColumns] = useState(0);
  const [sensitiveTables, setSensitiveTables] = useState(0);
  const [sensitiveColumns, setSensitiveColumns] = useState(0);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [searchid, setSearchid] = useState('');
  const [fetchingData, setFetchingData] = useState(true);
  const [engine, setEngine] = useState('');
  const [sourceCategory, setSourceCategory] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [totalSensitiveData, setTotalSensitiveData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [currentRiskScore, setCurrentRiskScore] = useState(1);
  const [currentRiskLabel, setCurrentRiskLabel] = useState('UNKNOWN');
  const [latestDiscoverySearchid, setLatestDiscoverySearchid] = useState('');
  const [isFileBased, setIsFileBased] = useState(false);
  const [hasDiscovery, setHasDiscovery] = useState(false);
  const [cluster, setCluster] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  // The connection row for lock state — the risk-overview payload doesn't carry readOnly.
  const connection = (connections || []).find(
    (c) => getDataSourceNumericId({ id: c.id, tableData: { id: c.id } }) === Number(id)
  );
  const readOnly = Boolean(connection?.readOnly);

  useEffect(() => {
    const init = async () => {
      setFetchingData(true);
      setError(null);
      try {
        await fetchDataHandler(id);
      } catch (e) {
        console.log(e);
        setError(e.message);
      }
      setFetchingData(false);
    };
    if (id) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDataHandler = async (dataSourceId) => {
    // Hub feeds are independent of discovery: 404 = not profiled/clustered yet.
    const [clusterValue, summaryValue] = await Promise.all([
      getIdentityCluster(dataSourceId).catch(() => null),
      getDataSourceSummary(dataSourceId).catch(() => null),
    ]);
    setCluster(clusterValue);
    setSummary(summaryValue);

    let sourceProfile = null;
    try {
      sourceProfile = await getProfileSource(dataSourceId);
    } catch (e) {
      console.log('risk-overview lookup failed for hub', e);
    }

    setSourceName(
      sourceProfile?.name ||
        connection?.name ||
        (summaryValue?.connection && summaryValue.connection.name) ||
        ''
    );
    setEngine(sourceProfile?.engine || summaryValue?.engine || connection?.type || '');
    setSourceCategory(sourceCategoryLabel(sourceProfile?.datasourceType || connection?.datasourceType));
    setIsFileBased(isFileBasedDatasourceType(sourceProfile?.datasourceType || connection?.datasourceType));
    setCurrentRiskScore(typeof sourceProfile?.latestRiskScore === 'number' ? sourceProfile.latestRiskScore : 1);
    setCurrentRiskLabel(normalizeRiskLevel(sourceProfile?.latestRiskLevel));

    // Prefer the last SUCCEEDED run — risk-overview's latestDiscoveryId is the newest
    // run of ANY status, which after a cancel/failure points at partial results.
    const searchidValue =
      (await getLatestSuccessfulDiscoveryId(dataSourceId)) ||
      (sourceProfile?.latestDiscoveryId != null ? String(sourceProfile.latestDiscoveryId) : '');
    if (!searchidValue) {
      setHasDiscovery(false);
      return;
    }
    setHasDiscovery(true);

    const [latestResults, timeSeriesDataValue] = await Promise.all([
      getLatestDiscoverResults(searchidValue),
      getSearchHistogram(dataSourceId),
    ]);

    // Prefer the worker's precomputed summary; fall back to deriving from tables for runs
    // that predate it (and for classic relational payloads, which carry tables directly).
    const tables = Array.isArray(latestResults?.payload?.tables) ? latestResults.payload.tables : [];
    const summaryStats = latestResults?.runSummary
      ? findingsSummaryFromRunSummary(latestResults.runSummary)
      : getFindingsSummary(tables);
    const categories = transformCategoryData(summaryStats.categoryScores, 0);

    const { categoryDataValue, totalSensitiveDataValue } = categories;
    const { totalTablesValue, totalFieldsValue, sensitiveTablesValue, sensitiveFieldsValue, findingsValue } =
      summaryStats;

    setFindings(findingsValue);
    setEmptyTables(Number(latestResults?.emptyTablesSkipped) || 0);
    // Index-mode runs feed getFindingsSummary a SPARSE table list (sensitive only), so
    // whole-run table/field totals come from the index instead of the summary counts.
    setTotalTables(latestResults?.indexTotals ? latestResults.indexTotals.totalTables : totalTablesValue);
    setTotalColumns(latestResults?.indexTotals ? latestResults.indexTotals.totalFields : totalFieldsValue);
    setSensitiveTables(sensitiveTablesValue);
    setSensitiveColumns(sensitiveFieldsValue);
    setSearchid(searchidValue);
    setTimeSeriesData(timeSeriesDataValue);
    setCategoryData(categoryDataValue);
    setTotalSensitiveData(totalSensitiveDataValue);

    // Extract latest searchid from discovery results over time
    const latestSearchid =
      timeSeriesDataValue && timeSeriesDataValue.length > 0
        ? timeSeriesDataValue[timeSeriesDataValue.length - 1].searchids[0]
        : searchidValue;
    setLatestDiscoverySearchid(latestSearchid);
  };

  const currentMember = cluster?.dataSources?.find((m) => String(m.dataSourceId) === String(id)) || null;

  // Item/container nouns for this source ("tables" vs "files" vs "collections"). engine
  // distinguishes mongo; isFileBased (already resolved from datasourceType) forces the file
  // vocabulary regardless of the opaque engine string file rows carry on the wire.
  const nomenclature = getSourceNomenclature(engine, isFileBased ? 'FILE_FOLDER' : undefined);

  // Free text from the source editor (connection_details.description on the data source row).
  const description = typeof connection?.description === 'string' ? connection.description.trim() : '';

  // Categories for the risk level the source landed on — the copy the retired "Current risk"
  // thermometer panel carried, now the Risk level KPI tile's sub-line.
  const riskCategories = (RISK_LEVELS[currentRiskScore] && RISK_LEVELS[currentRiskScore].categories) || [];

  // Derived tags — tags aren't a first-class backend concept yet (see the rework doc);
  // they come off read-only state, cluster family, and the latest risk level.
  const tags = [];
  tags.push(
    readOnly
      ? { label: 'masking-source', color: '#6D28D9', bg: 'var(--op-mask-bg)' }
      : { label: 'masking-target', color: STATUS.info.text, bg: STATUS.info.bg }
  );
  if (cluster?.displayName) {
    tags.push({ label: cluster.displayName, color: '#0369A1', bg: 'var(--op-synth-bg)' });
  }
  if (connection?.registeredServiceId) {
    tags.push({
      label: `Imported from ${connection.registeredServiceName || getSourceLabel(connection.registeredServiceId)}`,
      color: '#6D28D9',
      bg: 'var(--op-mask-bg)',
    });
  }
  if (currentRiskLabel === 'CRITICAL') {
    tags.push({ label: 'pii:critical', color: STATUS.danger.text, bg: STATUS.danger.bg });
  }

  return (
    <FetchContainer fetchingData={fetchingData} absolute={true} error={error}>
      <div>
        <div className='title-div'>
          <PageHeader
            header={
              <span>
                {sourceName || `Data source #${id}`}{' '}
                <span
                  className='sg-pill sg-pill--nodot'
                  style={
                    readOnly
                      ? { background: STATUS.warning.bg, color: STATUS.warning.text, verticalAlign: 'middle' }
                      : { background: STATUS.success.bg, color: STATUS.success.text, verticalAlign: 'middle' }
                  }
                  data-testid='hub-lock-pill'
                >
                  {readOnly ? <FaLock size='0.8em' /> : <FaLockOpen size='0.8em' />}{' '}
                  {readOnly ? 'Masking locked' : 'Maskable'}
                </span>
              </span>
            }
            // Whatever the user wrote in the source editor, under the name. Blank sources render
            // nothing at all rather than an empty line (PageHeader drops a falsy meta).
            meta={description ? (
              <span className='sg-small' style={{ color: INK[500] }} data-testid='hub-description'>
                {description}
              </span>
            ) : undefined}
            tooltip={'The full history, meta profile, identity cluster and risk for this data source.'}
            icon={FaChartBar}
            actions={[
              // We're already ON a data source — the actions run against it directly instead
              // of routing through the data-source picker.
              <Dropdown.Item
                key='new-discovery'
                disabled={actionBusy}
                data-testid='hub-action-new-discovery'
                onClick={async () => {
                  setActionBusy(true);
                  try {
                    const gate = await ensureConnectionTested(Number(id), {
                      history,
                      setNotification,
                      name: sourceName,
                    });
                    if (!gate.ok) {
                      setActionBusy(false);
                      return;
                    }
                    await startDiscoveryWithRule(Number(id));
                    if (setNotification) {
                      setNotification({
                        header: 'Discovery started',
                        body: `Discovery is now running for ${sourceName || `data source #${id}`}.`,
                      });
                    }
                  } catch (e) {
                    console.log('New discovery from risk profile failed', e);
                    if (setNotification) {
                      setNotification({ header: 'Discovery failed to start', body: e?.message || String(e) });
                    }
                  }
                  setActionBusy(false);
                }}
              >
                New Discovery
              </Dropdown.Item>,
              <Dropdown.Item
                key='new-profile'
                disabled={actionBusy}
                data-testid='hub-action-new-profile'
                onClick={async () => {
                  setActionBusy(true);
                  try {
                    const gate = await ensureConnectionTested(Number(id), {
                      history,
                      setNotification,
                      name: sourceName,
                    });
                    if (!gate.ok) {
                      setActionBusy(false);
                      return;
                    }
                    await startProfileJob(Number(id));
                    if (setNotification) {
                      setNotification({
                        header: 'Profile started',
                        body: `A meta scan is now running for ${sourceName || `data source #${id}`}.`,
                      });
                    }
                  } catch (e) {
                    console.log('New profile from risk profile failed', e);
                    if (setNotification) {
                      setNotification({ header: 'Profile failed to start', body: e?.message || String(e) });
                    }
                  }
                  setActionBusy(false);
                }}
              >
                New Profile
              </Dropdown.Item>,
              // Read-only sources CAN be masked cross-source — the inventory page locks
              // in-place and requires picking a destination, so this stays enabled.
              <Dropdown.Item
                key='new-mask'
                data-testid='hub-action-new-mask'
                onClick={() => history.push(`/secure/mask/inventory/${encodeURIComponent(id)}`)}
              >
                New Masking Job
              </Dropdown.Item>,
            ]}
          />
          <p>
            <span>
              {engine ? <EngineIcon engine={engine} /> : null}
              {engine ? ` ${engineLabels[engine] || engine} · ${sourceCategory}` : sourceCategory}
            </span>
          </p>
          <div className='d-flex mb-2' style={{ gap: 6 }}>
            {tags.map((tag) => (
              <span
                key={tag.label}
                className='sg-tag'
                style={{ background: tag.bg, color: tag.color, fontFamily: FONT_MONO }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>

        <HubKpiStrip
          riskLabel={currentRiskLabel}
          cluster={cluster}
          currentMember={currentMember}
          summary={summary}
          findingTypeCount={hasDiscovery ? findings.length : null}
          riskCategories={riskCategories}
          tableCount={hasDiscovery ? totalTables : null}
          emptyTableCount={hasDiscovery ? emptyTables : 0}
          fieldCount={hasDiscovery ? totalColumns : null}
          sensitiveTableCount={hasDiscovery ? sensitiveTables : null}
          sensitiveFieldCount={hasDiscovery ? sensitiveColumns : null}
          nomenclature={nomenclature}
        />

        {hasDiscovery ? (
          <div>
            <div><h5 className='text-left sg-eyebrow'>Risk summary</h5></div>
            <Card>
              <Card.Body>
                <Row className='mb-1'>
                  <Col className='flex-grow-1 pl-1 pr-1'>
                    <RiskCategories
                      searchid={latestDiscoverySearchid}
                      categoryData={categoryData}
                      totalSensitiveData={totalSensitiveData}
                    />
                  </Col>
                  <Col xs='3' className='pl-1 pr-1 w-50'>
                    <FlaggedFields
                      totalTables={totalTables}
                      totalColumns={totalColumns}
                      sensitiveTables={sensitiveTables}
                      sensitiveColumns={sensitiveColumns}
                      isFileBased={isFileBased}
                      nomenclature={nomenclature}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Row className='mb-1'>
              <Col>
                <TimeSeriesChart
                  searchid={searchid}
                  findings={findings}
                  fetchingData={props.fetchingData}
                  timeSeriesData={timeSeriesData}
                  tableLabel={nomenclature.itemSingular}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <Card className='mb-3'>
            <Card.Body>
              <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
                No discovery results yet for this data source — the risk summary appears after the first
                discovery run.
              </div>
            </Card.Body>
          </Card>
        )}

        <ActivityHistory dataSourceId={id} />
        <SubSourcesPanel parentId={Number(id)} />
        <IdentityClusterPanel cluster={cluster} currentId={id} />
        <MaskingPanel dataSourceId={id} readOnly={readOnly} />
      </div>
    </FetchContainer>
  );
};

export default DataSourceRiskProfile;
